<script lang="ts">
	import { createDroppable, type CreateDroppableInput } from "@dnd-kit/svelte";
	import { FieldType, type Field } from "#gen/forms/v1/field_pb.js";
	import type { Form } from "#gen/forms/v1/forms_pb.js";
	import Address from "#lib/components/page/components/form/Address.svelte";
	import Choice from "#lib/components/page/components/form/Choice.svelte";
	import Date from "#lib/components/page/components/form/Date.svelte";
	import EmailField from "#lib/components/page/components/form/EmailField.svelte";
	import FieldEditor from "#lib/components/page/components/form/FieldEditor.svelte";
	import FileUpload from "#lib/components/page/components/form/FileUpload.svelte";
	import FormHeading from "#lib/components/page/components/form/Heading.svelte";
	import NameField from "#lib/components/page/components/form/NameField.svelte";
	import NumberField from "#lib/components/page/components/form/NumberField.svelte";
	import PhoneField from "#lib/components/page/components/form/PhoneField.svelte";
	import PolicyField from "#lib/components/page/components/form/PolicyField.svelte";
	import TextField from "#lib/components/page/components/form/TextField.svelte";
	import { getTypeIcon } from "#lib/components/page/components/form/fieldTypes.js";
	import clickOutside from "#lib/directives/clickOutside.js";

	interface Props extends CreateDroppableInput {
		form: Form;
		fields: Field[];
		onDuplicate?: (fieldName: string) => void;
		onRemove?: (fieldName: string) => void;
		onMoveUp?: (fieldName: string) => void;
		onMoveDown?: (fieldName: string) => void;
		onInput?: (field: Field) => void;
		onChange?: (field: Field) => void;
	}

	let { form = $bindable(), fields = $bindable(), onInput, onChange, onDuplicate, onRemove, onMoveUp, onMoveDown, ...rest }: Props = $props();
	let activeField = $state("");
	const advancedOpen: Record<string, boolean> = $state(Object.fromEntries(form.fields.map(field => [field.name, false])));

	const { attach } = $derived(createDroppable(rest));

	function handleFieldInputEvent(event: CustomEvent<Field>) {
		handleFieldInput(event.detail);
	}

	function handleFieldInput(field: Field) {
		onInput?.(field);
	}

	function handleFieldChangeEvent(event: CustomEvent<Field>) {
		handleFieldChange(event.detail);
	}

	function handleFieldChange(field: Field) {
		onChange?.(field);
	}

	function handleRemoved(fieldName: string) {
		delete advancedOpen[fieldName];
		onRemove?.(fieldName);
	}

	function handleAdvancedToggle(field: Field, open: boolean) {
		advancedOpen[field.name] = open;
	}
</script>

<form class="field-dnd-container flex flex-col gap-4" {@attach attach} use:clickOutside={() => (activeField = "")}>
	{#each fields as field, index (`${field.name}:${index}`)}
		{@const active = activeField == field.name}
		{@const typeIcon = getTypeIcon(field.fieldType, field.properties)}
		<FieldEditor id={field.name} {index} total={fields.length} {active} editable showAdvanced={advancedOpen[field.name] ?? false} onClick={() => (activeField = field.name)} {field} onRemove={handleRemoved} {onDuplicate} {onMoveUp} {onMoveDown} formData={form} onChange={handleFieldChange} onAdvancedToggle={handleAdvancedToggle}>
			{#if field.fieldType === FieldType.TEXT}
				<TextField id={field.id} {typeIcon} {active} editable {field} on:input={handleFieldInputEvent} on:change={handleFieldChangeEvent} />
			{:else if field.fieldType === FieldType.BLOCK_TEXT}
				<FormHeading id={field.id} {typeIcon} editable {field} on:input={handleFieldInputEvent} on:change={handleFieldChangeEvent} />
			{:else if field.fieldType === FieldType.EMAIL}
				<EmailField id={field.id} {typeIcon} {active} editable {field} on:input={handleFieldInputEvent} on:change={handleFieldChangeEvent} />
			{:else if field.fieldType === FieldType.NAME}
				<NameField id={field.id} {typeIcon} {active} editable {field} on:input={handleFieldInputEvent} on:change={handleFieldChangeEvent} />
			{:else if field.fieldType === FieldType.PHONE}
				<PhoneField id={field.id} {typeIcon} {active} editable {field} on:input={handleFieldInputEvent} on:change={handleFieldChangeEvent} />
			{:else if field.fieldType === FieldType.ADDRESS}
				<Address id={field.id} {typeIcon} {active} editable {field} on:input={handleFieldInputEvent} on:change={handleFieldChangeEvent} />
			{:else if field.fieldType === FieldType.CHOICE}
				<Choice id={field.id} {typeIcon} {active} editable {field} onInput={handleFieldInput} onChange={handleFieldChange} />
			{:else if field.fieldType === FieldType.DATE}
				<Date id={field.id} {typeIcon} {active} editable {field} on:input={handleFieldInputEvent} on:change={handleFieldChangeEvent} />
			{:else if field.fieldType === FieldType.FILE_UPLOAD}
				<FileUpload id={field.id} {typeIcon} {active} editable {field} onChange={handleFieldChange} />
			{:else if field.fieldType === FieldType.NUMBER}
				<NumberField id={field.id} {typeIcon} {active} editable {field} on:input={handleFieldInputEvent} on:change={handleFieldChangeEvent} />
			{:else if field.fieldType === FieldType.LEGAL}
				<PolicyField id={field.id} {typeIcon} {active} editable {field} onInput={handleFieldInput} onChange={handleFieldChange} />
			{/if}
		</FieldEditor>
	{/each}
</form>

<style>
	.field-dnd-container :global([data-dnd-placeholder]) {
		visibility: visible !important;
		background-color: var(--color-primary-100);
		border-radius: 0.5rem;
	}

	.field-dnd-container :global([data-dnd-placeholder] *) {
		visibility: hidden !important;
	}
</style>
